import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { clientQuery } from 'Core/data/GraphQLApi';
import { ExceptionManager } from 'Core/logManager';
import { endpoints } from 'Core/defaultValues';
import { NotificationManager } from 'Components/Notifications';
import { speedFormatMPH, deleteDupplyInArray } from 'Core/data/Helpers';
import { getApiDate } from '../../../../../../core/hooks/useUnitData';
import { orderTrailPolilyne } from '../../../../../../components/Map/utils'


//Action types
import { REALTIME_MAP_VEHICLETRAILS_GET, REALTIME_MAP_VEHICLETRAILS_SEARCH_UNIT_TYPE_EVENTS_GET, GET_LAST_VEHICLETRAILS } from 'Redux/actionTypes';

//actions
import { getVehicleTrailsConfirmResult, searchUnitTypeEventsResult, getLastVehicleTrailResult } from 'Redux/actions';
import { isArray } from 'lodash';

//GraphQL query for searchVehicleTrails
const SEARCH_VEHICLE_TRAILS_QUERY = `query searchVehicleTrails ($startDate: AWSDateTime!, $endDate: AWSDateTime!, $event: VehicleTrailEvent, $deviceId: String!, $openSearch: String, $limit: Int, $offset: Int, $includeLinkedData: Boolean, $withRecentEvent: Boolean) {
  Trails: searchVehicleTrails (startDate: $startDate, endDate: $endDate, event: $event, deviceId: $deviceId, openSearch: $openSearch, limit: $limit, offset: $offset, includeLinkedData: $includeLinkedData, withRecentEvent: $withRecentEvent) {
      items {
          id
          deviceData
          deviceId
          latitude
          longitude
          heading
          eventCode
          eventName
          address
          formattedAddress
          driverId
          driverName
          ignitionStatus
          satelliteCount
          signal
          ecuSpeed
          speed
          speedLimit
          unitTime
          landmark {
            id
            companyId
            name
            tags {
              id
              label
              color
              path
            }
            address
            city
            zip
            state
            country
          }
          trail{
            lat
            lon
            timestamp
          }
      }
      total
  }
}`;

/**
 * Transforms raw trail items: sorts, marks children, assigns waypoints/speed.
 */
function processTrailItems(allItems, countItemsStart, useEcuSpeed, unitType, kph) {
  let countItems = countItemsStart;
  let wayPoint = allItems.length + 1;

  // Sort ascending by unitTime
  let dataTrails = [...allItems].sort((a, b) => {
    return parseInt(getApiDate(a.unitTime)) - parseInt(getApiDate(b.unitTime));
  });

  // Build trail route from unsorted items
  const trailRoute = orderTrailPolilyne([...allItems]);

  // Mark children (consecutive same eventName)
  let lastEvent = '', isChild = false;
  dataTrails = dataTrails.map((item) => {
    if (lastEvent != item.eventName) {
      lastEvent = item.eventName;
      isChild = false;
    } else {
      isChild = true;
    }
    return { ...item, hideMarker: isChild };
  });

  // Sort descending by unitTime
  dataTrails = dataTrails.sort((a, b) => {
    return parseInt(getApiDate(b.unitTime)) - parseInt(getApiDate(a.unitTime));
  });

  // Assign index, wayPoint, speed
  dataTrails = dataTrails.map((item) => {
    countItems++;
    wayPoint--;
    let speed = useEcuSpeed && item?.ecuSpeed > 0 ? item?.ecuSpeed : item.speed;
    let speedFormat = 0;
    if (unitType && kph) {
      speedFormat = speedFormatMPH(speed, unitType, kph, true);
    }
    return { ...item, index: countItems, wayPoint: wayPoint, speedFormat: speedFormat, speed: speed, unitType: unitType };
  });

  return { dataTrails, trailRoute };
}

//Function
function* getVehicleTrailsRequest({ payload }) {
  const { params } = payload
  const { unitId, event, startDate, endDate, openSearch, limit, offset, useEcuSpeed, unitType, kph, withRecentEvent, isATrip } = params
  let countItems = offset == 0 ? offset : (offset - 1) * limit; //starting at the offset element
  let queryElements = { deviceId: unitId, event, startDate, endDate, openSearch: openSearch ? openSearch : '', limit, offset: countItems, includeLinkedData: true, withRecentEvent }
  
  if (event == '0') {
    delete queryElements["event"]
  }
  if (!openSearch) {
    delete queryElements["openSearch"]
  }
  try {
    const response = yield call(
      clientQuery,
      SEARCH_VEHICLE_TRAILS_QUERY,
      queryElements,
      endpoints.GRAPHQL_GENERAL,
    );

    let data = {}, trailRoute = [];
    if (response && response.Trails) {
      let allItems = response.Trails.items || [];
      const total = response.Trails.total || 0;

      // When loading a trip and there are more items, fetch all remaining pages
      if (isATrip && total > allItems.length) {
        let fetchedCount = allItems.length;
        while (fetchedCount < total) {
          const nextResponse = yield call(
            clientQuery,
            SEARCH_VEHICLE_TRAILS_QUERY,
            { ...queryElements, offset: fetchedCount },
            endpoints.GRAPHQL_GENERAL,
          );
          if (nextResponse?.Trails?.items?.length) {
            allItems = allItems.concat(nextResponse.Trails.items);
            fetchedCount = allItems.length;
          } else {
            break;
          }
        }
      }

      if (allItems.length) {
        const { dataTrails, trailRoute: route } = processTrailItems(allItems, countItems, useEcuSpeed, unitType, kph);
        trailRoute = route;
        data = { ...response.Trails, total, items: dataTrails, trailRoute: trailRoute };
      }

      yield put(getVehicleTrailsConfirmResult(data));
    } else {
      throw 'Not Vehicle Trails';
    }
  } catch (e) {
    ExceptionManager(e, 'modules/maintenance/views/redux/saga', 'getVehicleTrailsRequest');
    yield put(getVehicleTrailsConfirmResult(null));
  }
}

function* searchUnitTypeEvents({ payload }) {
  try {
    const response = yield call(
      clientQuery,
      `query searchUnitTypeEvents ($unitTypeId: Int) {
        result: searchUnitTypeEvents (unitTypeId: $unitTypeId) {
          eventName
          eventId
        }
      }`,
      {
        unitTypeId: payload
      },
      endpoints.GRAPHQL_GENERAL,
    );
    if (response?.result) {
      yield put(searchUnitTypeEventsResult(response?.result))
    } else yield put(searchUnitTypeEventsResult())
  } catch (e) {
    yield put(searchUnitTypeEventsResult())
    ExceptionManager(e, 'modules/realtimeMaps/main/components/VehicleTrails/redux/saga', 'searchUnitTypeEvents');
  }
}

function* getLastVehicleTrailsRequest({ payload }) {
  const { params } = payload
  const { unitId, event, date, useEcuSpeed, comeBack,  unitType, kph } = params
  let wayPoint = 0
  let queryElements = { deviceId: unitId, date, comeBack, includeLinkedData: true }
  if (event == '0') {
    delete queryElements["event"]
  }
  try {
    const response = yield call(
      clientQuery,
      `query searchLastVehicleTrail ($date: AWSDateTime!, $deviceId: String!, $includeLinkedData: Boolean, $comeBack: Int) {
        Trails:searchLastVehicleTrail (date: $date, deviceId: $deviceId, includeLinkedData: $includeLinkedData, comeBack: $comeBack) {
            items {
                id
                deviceId
                latitude
                longitude
                heading
                eventCode
                eventName
                address
                formattedAddress
                driverId
                driverName
                ignitionStatus
                satelliteCount
                signal
                ecuSpeed
                speed
                speedLimit
                unitTime
                landmark {
                  id
                  companyId
                  name
                  tags {
                    id
                    label
                    color
                    path
                  }
                  address
                  city
                  zip
                  state
                  country
                }
            }
            total
        }
    }`,
      queryElements,
      endpoints.GRAPHQL_GENERAL,
    );

    let data = {};
    if (response && response.Trails) {
      const vehicleTrailsData = response.Trails;

      if (vehicleTrailsData && vehicleTrailsData.items) {
        wayPoint = vehicleTrailsData.items.length + 1
        const dataTrails = vehicleTrailsData.items.map((item) => {
          wayPoint--;
          let speed =  useEcuSpeed && item?.ecuSpeed > 0 ? item?.ecuSpeed : item.speed
          if(unitType && kph){
            speed = speedFormatMPH(speed, unitType, kph, true)
          }
          return { ...item, index: 1, wayPoint: wayPoint, speed: speed }
        });

        data = { ...response.Trails, items: dataTrails };
      }

      yield put(getLastVehicleTrailResult(data));
    } else {
      throw 'Not Vehicle Trails';
    }
  } catch (e) {
    ExceptionManager(e, 'modules/maintenance/views/redux/saga', 'getLastVehicleTrailRequest');
    yield put(getLastVehicleTrailResult(null));
  }
}

function* watchGetVehicleTrailsTasksRequest() {
  yield takeEvery(REALTIME_MAP_VEHICLETRAILS_GET, getVehicleTrailsRequest);
  yield takeEvery(REALTIME_MAP_VEHICLETRAILS_SEARCH_UNIT_TYPE_EVENTS_GET, searchUnitTypeEvents);
  yield takeEvery(GET_LAST_VEHICLETRAILS, getLastVehicleTrailsRequest)
}
//Saga Export
export default function* rootSaga() {
  yield all([
    fork(watchGetVehicleTrailsTasksRequest)
  ]);
}